import { useElementSize } from "@mantine/hooks";

import type { PileObsOut, SiteOut } from "../../api/generated/model";
import { palette } from "../../palette";
import { arcBox, arcPath, midBearing, polar, spoke, sweepDeg, wedgePath } from "./geometry";
import { fmt0 } from "./tables";

const RING_M = 25;

/**
 * The stockyard from above, north up, one SVG unit = one metre: the sector fan with range rings,
 * the arc the last sweep covered, the boom where it ended, and each pile as the wedge of its
 * bearings and radii, coloured by product and labelled. Hovering a pile highlights it (and its
 * row in the table); clicking opens its history.
 */
export function PolarArc({
  site,
  piles,
  coverage,
  boom,
  colourOf,
  hovered = null,
  selected = null,
  onHover,
  onPile,
}: {
  site: SiteOut;
  piles: PileObsOut[];
  coverage: { from: number; to: number } | null;
  boom?: number | null;
  colourOf: (product: string | null | undefined) => string;
  hovered?: string | null;
  selected?: string | null;
  onHover?: (code: string | null) => void;
  onPile?: (code: string) => void;
}) {
  const { ref, width } = useElementSize();
  const dir = site.arc.direction >= 0 ? 1 : -1;
  const { inner_radius_m: rIn, outer_radius_m: rOut, boundaries_bearing_deg: bounds } = site.sectors;
  const pad = 14;
  const box = arcBox(site.arc.start_bearing_deg, site.arc.end_bearing_deg, rOut + pad, dir);
  const vb = { x: box.x - 4, y: box.y - 22, w: box.w + 8, h: box.h + 26 };
  // pixels per metre (jsdom has no layout: a fixed guess), so text and strokes keep their size
  const k = width > 0 ? Math.min(width / vb.w, 460 / vb.h) : 2;
  const px = (n: number) => n / k;
  const rings: number[] = [];
  for (let r = Math.ceil((rIn + 1) / RING_M) * RING_M; r < rOut - 2; r += RING_M) rings.push(r);
  const ringLabelAt = site.arc.start_bearing_deg - dir * 1.5;
  const focus = hovered ?? selected;
  // a sweep may run against the arc's direction: draw its wedge the short way, inside the arc
  const span = sweepDeg(site.arc.start_bearing_deg, site.arc.end_bearing_deg, dir);
  const cov =
    coverage && sweepDeg(coverage.from, coverage.to, dir) > span + 1
      ? { from: coverage.to, to: coverage.from }
      : coverage;

  return (
    <div ref={ref} style={{ width: "100%" }}>
      <svg
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        width="100%"
        style={{ maxHeight: 460, display: "block" }}
        role="img"
        aria-label="Plan view of the stockyard"
        data-testid="polar-arc"
        onMouseLeave={() => onHover?.(null)}
      >
        {/* the yard */}
        <path
          d={wedgePath(site.arc.start_bearing_deg, site.arc.end_bearing_deg, rIn, rOut, dir)}
          fill={palette.plan.yard}
          stroke={palette.plan.yardStroke}
          strokeWidth={px(1)}
          data-testid="yard"
        />
        {/* what the last sweep saw */}
        {cov && (
          <path
            d={wedgePath(cov.from, cov.to, rIn, rOut, dir)}
            fill={palette.plan.coverage}
            fillOpacity={0.75}
            stroke="none"
            data-testid="coverage"
          />
        )}
        {/* range rings */}
        {rings.map((r) => {
          const at = polar(ringLabelAt, r);
          return (
            <g key={`r${r}`}>
              <path
                d={arcPath(site.arc.start_bearing_deg, site.arc.end_bearing_deg, r, dir)}
                fill="none"
                stroke={palette.plan.ring}
                strokeWidth={px(0.8)}
                strokeDasharray={`${px(3)} ${px(3)}`}
              />
              <text
                x={at.x}
                y={at.y}
                fontSize={px(10)}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={palette.muted}
              >
                {r} m
              </text>
            </g>
          );
        })}
        {/* sector boundaries and numbers */}
        {bounds.map((b, i) => {
          const [a, c] = spoke(b, rIn, rOut);
          return (
            <line key={`b${i}`} x1={a.x} y1={a.y} x2={c.x} y2={c.y} stroke={palette.plan.spoke} strokeWidth={px(0.8)} />
          );
        })}
        {bounds.slice(0, -1).map((b, i) => {
          const next = bounds[i + 1] ?? b;
          const p = polar(midBearing(b, next, dir), rOut + 6);
          return (
            <text
              key={`s${i}`}
              x={p.x}
              y={p.y}
              fontSize={px(10)}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={palette.muted}
              data-testid={`sector-label-${i + 1}`}
            >
              {i + 1}
            </text>
          );
        })}
        {/* the piles */}
        {piles.map((p) => {
          const on = focus === p.code;
          const dim = focus !== null && !on;
          return (
            <path
              key={p.code}
              d={wedgePath(p.slew_min_deg, p.slew_max_deg, p.r_min_m, p.r_max_m, 1)}
              fill={colourOf(p.product)}
              fillOpacity={dim ? 0.35 : 0.88}
              stroke={on ? palette.text : "#fff"}
              strokeWidth={px(on ? 2 : 1)}
              style={{ cursor: onPile ? "pointer" : undefined, transition: "fill-opacity 120ms" }}
              onClick={() => onPile?.(p.code)}
              onMouseEnter={() => onHover?.(p.code)}
              data-testid={`pile-${p.code}`}
            />
          );
        })}
        {/* the boom where the last sweep ended */}
        {boom !== null && boom !== undefined && (
          <g pointerEvents="none">
            {(() => {
              const end = polar(boom, rOut - 4);
              return (
                <>
                  <line
                    x1={0}
                    y1={0}
                    x2={end.x}
                    y2={end.y}
                    stroke={palette.plan.boom}
                    strokeWidth={px(2)}
                    strokeDasharray={`${px(6)} ${px(4)}`}
                    strokeLinecap="round"
                  />
                  <circle cx={end.x} cy={end.y} r={px(4)} fill={palette.plan.boom} stroke="#fff" strokeWidth={px(1.5)} />
                </>
              );
            })()}
          </g>
        )}
        {/* pile labels: the name, the volume */}
        {piles.map((p) => {
          const at = polar(midBearing(p.slew_min_deg, p.slew_max_deg, 1), (p.r_min_m + p.r_max_m) / 2);
          const name = p.label ?? p.code.split("-").pop() ?? p.code;
          const v = p.volume?.v_best_m3;
          return (
            <g key={`l${p.code}`} pointerEvents="none" data-testid={`pile-label-${p.code}`}>
              <text
                x={at.x}
                y={at.y - px(7)}
                fontSize={px(12)}
                fontWeight={650}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#fff"
                stroke="rgba(10, 20, 30, 0.45)"
                strokeWidth={px(2.5)}
                paintOrder="stroke"
              >
                {name}
              </text>
              {v !== undefined && v !== null && (
                <text
                  x={at.x}
                  y={at.y + px(8)}
                  fontSize={px(10.5)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fff"
                  stroke="rgba(10, 20, 30, 0.45)"
                  strokeWidth={px(2.5)}
                  paintOrder="stroke"
                >
                  {fmt0(v)} m³
                </text>
              )}
            </g>
          );
        })}
        {/* the pivot and north */}
        <circle cx={0} cy={0} r={px(4)} fill={palette.plan.pivot} />
        <g pointerEvents="none">
          <line x1={0} y1={-px(8)} x2={0} y2={-px(26)} stroke={palette.muted} strokeWidth={px(1.2)} />
          <path
            d={`M 0 ${-px(32)} L ${px(4)} ${-px(24)} L ${-px(4)} ${-px(24)} Z`}
            fill={palette.muted}
          />
          <text x={px(9)} y={-px(27)} fontSize={px(10)} fill={palette.muted} dominantBaseline="middle">
            N
          </text>
        </g>
      </svg>
    </div>
  );
}
